

import type { StructureSection, StructureLayoutType } from '../types';

/**
 * Calculate node positions for different layout types
 */
export function calculatePositions(
  sections: StructureSection[],
  layoutType: StructureLayoutType,
  canvasWidth: number = 1200,
  canvasHeight: number = 800
): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();
  const nodeWidth = 180;
  const nodeHeight = 80;
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;

  // Filter out unassigned section for layout calculation
  const layoutSections = sections.filter(s => s.id !== 'unassigned');
  const count = layoutSections.length;

  if (count === 0) return positions;

  // Position for unassigned (always at far left)
  const unassigned = sections.find(s => s.id === 'unassigned');
  if (unassigned) {
    positions.set(unassigned.id, { x: 30, y: centerY - nodeHeight / 2 });
  }

  switch (layoutType) {
    case 'circular': {
      // Circular layout - like Hero's Journey
      const radiusX = Math.max(350, (canvasWidth - nodeWidth - 250) / 2);
      const radiusY = Math.max(280, (canvasHeight - nodeHeight - 150) / 2);
      const startAngle = -Math.PI / 2; // Start at top

      layoutSections.forEach((section, index) => {
        const angle = startAngle + (2 * Math.PI * index) / count;
        const x = centerX + radiusX * Math.cos(angle) - nodeWidth / 2 + 80;
        const y = centerY + radiusY * Math.sin(angle) - nodeHeight / 2;
        positions.set(section.id, { x, y });
      });
      break;
    }

    case 'linear': {
      // Horizontal linear layout
      const minSpacing = 220;
      const totalWidth = Math.max(canvasWidth - 200, count * minSpacing);
      const spacing = totalWidth / (count + 1);

      layoutSections.forEach((section, index) => {
        const x = 200 + spacing * (index + 0.5) - nodeWidth / 2;
        const y = centerY - nodeHeight / 2;
        positions.set(section.id, { x, y });
      });
      break;
    }

    case 'curve': {
      // Fichtean curve - rising action then falling
      const peakIndex = Math.floor(count * 0.75);
      const minSpacing = 200;
      const totalWidth = Math.max(canvasWidth - 250, count * minSpacing);
      const spacing = totalWidth / (count + 1);

      layoutSections.forEach((section, index) => {
        const x = 200 + spacing * (index + 0.5);
        let yOffset;
        if (index <= peakIndex) {
          yOffset = -200 * (index / peakIndex);
        } else {
          yOffset = -200 + 150 * ((index - peakIndex) / (count - peakIndex));
        }
        const y = canvasHeight / 2 + 80 + yOffset;
        positions.set(section.id, { x, y });
      });
      break;
    }

    case 'grid':
    default: {
      // Grid layout - 3 columns
      const cols = 3;
      const colWidth = Math.max(250, (canvasWidth - 200) / cols);
      const rowHeight = 140;

      layoutSections.forEach((section, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);
        const x = 200 + colWidth * col + colWidth / 2 - nodeWidth / 2;
        const y = 60 + row * rowHeight;
        positions.set(section.id, { x, y });
      });
      break;
    }
  }

  return positions;
}
